// ============================================================
// ClientStatementModal.tsx — ★ a CLIENT STATEMENT (v1 — on demand).
// Pick which of the client's ISSUED bills of costs go on it (default: every
// one still outstanding), then preview / download it as a PDF through the
// bill PDF's letterhead + print path (utils/billPdf buildStatementHtml).
// Each bill stays its own VAT invoice — the statement writes nothing, numbers
// nothing and changes no balance. (On-account requests aren't listed: their
// money is credited onto a later bill, so listing both would count it twice.)
// ★ Money comes from the server ALREADY masked for this viewer, and its grand
// totals are worked out THERE — withheld (null) whenever any bill on the
// statement is partial / hidden for you. Nothing is summed here.
// The statement for the CURRENT selection is fetched ahead of the click, so
// "Preview / download" opens the print tab synchronously (no pop-up block).
// ============================================================
import { useLivePermissions } from '../../hooks/useLivePermissions'
import { ErrorNote } from '../shared/ActionBox'
import React, { useEffect, useMemo, useState } from 'react'
import ModalFrame from '../shared/ModalFrame'
import { getClientStatementApi } from '../../api/casevoApi'
import type { ClientStatement } from '../../types/billing.types'
import { printStatementPdf } from '../../utils/billPdf'
import WithheldAmount from '../shared/WithheldAmount'
import { useFirmLogos } from '../../utils/firmLogo'
import { BIcon, B_PRIMARY, B_SECONDARY, NOTE, TD, TH, Spinner, errorText, fmtDate, moneyOrHidden, partialMoney, Skel } from './billingUi'

const ClientStatementModal: React.FC<{ clientId: string; clientName: string; onClose: () => void }> = ({ clientId, clientName, onClose }) => {
  // Every eligible bill (the picker) + the statement for the current selection.
  const [all, setAll] = useState<ClientStatement | null>(null)
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const [statement, setStatement] = useState<{ key: string; data: ClientStatement } | null>(null)
  // Today's logo, loaded ahead of Print (the tab opens inside the click).
  useFirmLogos([all?.firm.logoId, statement?.data.firm.logoId])
  const [err, setErr] = useState<string | null>(null)
  // ★ Live permission change: a lost money right clears every figure here AT
  // ONCE, then both reads run again under the new rights (server-masked).
  const [nonce, setNonce] = useState(0)
  useLivePermissions((narrowed) => { if (narrowed) { setAll(null); setStatement(null) } setNonce((n) => n + 1) })
  useEffect(() => {
    let live = true
    getClientStatementApi(clientId).then((s) => {
      if (!live) return
      setAll(s)
      // Default: every bill still outstanding (by status — a hidden balance
      // still counts as outstanding when the bill is issued / part-paid).
      setPicked(new Set(s.bills.filter((b) => b.status === 'ISSUED' || b.status === 'PART_PAID').map((b) => b.id)))
    }).catch((e) => { if (live) setErr(errorText(e, 'Couldn’t load the client’s bills.')) })
    return () => { live = false }
  }, [clientId, nonce])
  // The selection, in a stable order (the server's), as the fetch key.
  const ids = useMemo(() => (all ? all.bills.filter((b) => picked.has(b.id)).map((b) => b.id) : []), [all, picked])
  const key = ids.join(',')
  useEffect(() => {
    if (!key) { setStatement(null); return }
    let live = true
    const t = setTimeout(() => {
      getClientStatementApi(clientId, key.split(',')).then((data) => { if (live) setStatement({ key, data }) })
        .catch((e) => { if (live) setErr(errorText(e, 'Couldn’t prepare the statement.')) })
    }, 200)
    return () => { live = false; clearTimeout(t) }
  }, [clientId, key, nonce])
  const ready = !!statement && statement.key === key && ids.length > 0
  const t = ready ? statement!.data.totals : null
  const toggle = (id: string): void => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const print = (): void => {
    if (!ready) return
    if (!printStatementPdf(statement!.data)) setErr('Your browser blocked the statement tab — allow pop-ups for Casevo.')
  }

  return (
    <ModalFrame onClose={onClose} size="lg" portal eyebrow="Client statement" eyebrowIcon={<BIcon name="bill" size={14} />}
      title={`Statement for ${clientName || 'this client'}`}
      subtitle="A summary of the bills you pick, as at today. Each bill stays its own invoice — the statement isn’t stored and changes nothing."
      footerRight={<>
        <button type="button" className={B_SECONDARY} onClick={onClose}>Close</button>
        <button type="button" className={B_PRIMARY} disabled={!ready} aria-busy={ids.length > 0 && !ready} onClick={print}>
          {ids.length > 0 && !ready ? <><Spinner />Preparing…</> : <><BIcon name="dl" size={14} />Preview / download PDF</>}
        </button>
      </>}>
      <div className="grid gap-3">
        {err && <ErrorNote className="">{err}</ErrorNote>}
        {/* ★ NO LAYOUT SHIFT: while the bills load, the same table (header + rows
            shaped like real ones) and the "N of M selected" line — not a one-line
            spinner the table then outgrew, which made the centred modal jump. */}
        {!all ? (!err && (
          <div role="status" aria-label="Loading the client’s bills">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13.5px] min-w-[600px]" aria-hidden="true">
                <thead><tr>
                  <th className={TH} style={{ width: 36 }} />
                  <th className={TH}>Matter</th><th className={TH}>Invoice</th><th className={TH}>Date</th>
                  <th className={`${TH} text-right`}>Total</th><th className={`${TH} text-right`}>Balance</th>
                </tr></thead>
                <tbody>
                  {[0, 1, 2].map((i) => (
                    <tr key={i}>
                      <td className={TD}><span className="inline-block w-[13px] h-[13px] rounded-[3px] bg-casevo-border-soft" /></td>
                      <td className={TD}><div className="font-medium"><Skel className="inline-block align-middle h-3 w-40" /></div><div className="text-[11.5px]"><Skel className="inline-block align-middle h-2.5 w-24" /></div></td>
                      <td className={`${TD} text-[12.5px]`}><Skel className="inline-block align-middle h-3 w-16" /></td>
                      <td className={`${TD} text-[12.5px]`}><Skel className="inline-block align-middle h-3 w-16" /></td>
                      <td className={`${TD} text-right`}><Skel className="inline-block align-middle h-3 w-14" /></td>
                      <td className={`${TD} text-right`}><Skel className="inline-block align-middle h-3 w-14" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[13px]"><Skel className="inline-block align-middle h-3 w-28" /></div>
          </div>
        ))
          : all.bills.length === 0 ? <p className={`${NOTE} m-0`}>No issued bills for this client that you can see.</p>
            : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13.5px] min-w-[600px]">
                  <thead><tr>
                    <th className={TH} style={{ width: 36 }}><span className="sr-only">Include</span></th>
                    <th className={TH}>Matter</th><th className={TH}>Invoice</th><th className={TH}>Date</th>
                    <th className={`${TH} text-right`}>Total</th><th className={`${TH} text-right`}>Balance</th>
                  </tr></thead>
                  <tbody>
                    {all.bills.map((b) => (
                      <tr key={b.id} className="cursor-pointer hover:bg-casevo-input" onClick={() => toggle(b.id)}>
                        <td className={TD}><input type="checkbox" checked={picked.has(b.id)} onChange={() => toggle(b.id)} onClick={(e) => e.stopPropagation()} aria-label={`Include ${b.number ?? 'bill'}`} /></td>
                        <td className={TD}><div className="font-medium text-casevo-forest">{b.case.name}</div><div className="text-[11.5px] text-casevo-text-soft font-mono">{b.case.ref}</div></td>
                        <td className={`${TD} font-mono text-[12.5px]`}>{b.number}</td>
                        <td className={`${TD} text-[12.5px] text-casevo-text-soft tabular-nums`}>{fmtDate(b.issuedAt)}</td>
                        <td className={`${TD} text-right tabular-nums`}>{partialMoney(b.totalPence, b.amountPartial)}</td>
                        <td className={`${TD} text-right tabular-nums`}>{moneyOrHidden(b.balancePence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        {all && all.bills.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-casevo-forest tabular-nums">
            <span>{ids.length} of {all.bills.length} selected</span>
            {ready && <>
              <span>Total billed <b>{moneyOrHidden(t!.totalPence)}</b></span>
              <span>Outstanding <b>{moneyOrHidden(t!.balancePence)}</b></span>
            </>}
          </div>
        )}
        {ready && t!.partial && (
          <p className={`${NOTE} m-0 flex items-start gap-[5px]`}><WithheldAmount size={11} />Some bills include colleagues’ billing hidden from you, so the statement’s totals are left blank and it’s marked as a restricted view — not one to send. Admins and colleagues with “See colleagues’ billing” see the full statement.</p>
        )}
      </div>
    </ModalFrame>
  )
}

export default ClientStatementModal
